import React, { useEffect, useRef, useState } from 'react';
import { Database, TrendingUp, Target, BarChart3 } from 'lucide-react';

const BigDataSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const features = [
    {
      icon: <Database className="w-8 h-8" />,
      title: 'Establish Baselines',
      description:
        'Set performance benchmarks to understand where you are before implementing solutions.',
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Measure Effectiveness',
      description:
        'Track and analyze the impact of your strategies to determine success rates.',
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Set Goals',
      description:
        'Use data insights to establish realistic, measurable objectives for growth.',
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: 'Keep Moving Forward',
      description:
        'Continuously optimize based on data to maintain competitive advantage.',
    },
  ];

  return (
    <section className="bg-white py-20" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Image with floating and zoom animation */}
          <div className={`relative order-2 md:order-1 transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'}`}>
            <div className="rounded-2xl overflow-hidden shadow-2xl group animate-float">
              <div className="relative overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?w=800&q=80"
                  alt="Big Data Analytics"
                  className="w-full h-auto object-cover transform group-hover:scale-110 group-hover:rotate-1 transition-all duration-700 ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#d97841]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={`space-y-8 order-1 md:order-2 transition-all duration-1000 delay-300 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'}`}>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Why is Big Data important
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Good data allows organizations to establish baselines, benchmarks, and
                goals to keep moving forward. Because data allows you to measure, you will
                be able to establish baselines, find benchmarks and set performance goals.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="p-6 rounded-xl bg-gradient-to-br from-[#0d6571]/5 to-[#0d6571]/10 border border-[#0d6571]/20 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="text-[#0d6571] mb-4">{feature.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BigDataSection;
